import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';
import React, { useContext, useEffect } from 'react';

import StatusInformationIcon from '@atlaskit/icon/core/status-information';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Inline, xcss, Stack, Text, Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';

import { InlineInvite } from '@atlassian/inline-invite';

import { CoreInvitesContext } from '@confluence/core-invites-provider/entry-points/CoreInvitesContext.ts';
import { getCurrentUrl } from '@confluence/core-invites-provider/entry-points/continueUrlHelpers.ts';
import { CoreInvitesSource } from '@confluence/core-invites-provider/entry-points/constants.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { fg } from '@confluence/feature-gating';
import { expValEquals, UNSAFE_expValNoExposure } from '@confluence/feature-experiments';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { updateCachedInvites } from '../../sessionStorage.tsx';
import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../../controller/Controller.tsx';
import { PressableLink } from '../../../PressableLink.tsx';

import type { PrincipalsStagerProps } from './PrincipalsStager.tsx';

const i18n = defineMessages({
	inviteMessage: {
		id: 'share-and-restrict-dialog.button.restriction.message.invite.text',
		defaultMessage:
			'Invite <b>{userEmail} {userCount, plural, =0 {} one {and # other} other {and # others}}</b> to join Confluence? Otherwise, we’ll just send a notification when you select Share.',
		description: 'Tells the user there might be restrictions on this content before sharing',
	},
	warningInviteMessage: {
		id: 'share-and-restrict-dialog.button.restriction.message.warning.invite.text',
		defaultMessage:
			'You can’t share specific content with people via email while on a trial or the Free plan. If you want to share via email, you’ll have to invite them to Confluence.',
		description:
			'Warning message shown when a user tries to share with email addresses while on the Free or trial plan, informing them of their options.',
	},
	invitePeopleCta: {
		id: 'share-and-restrict-dialog.button.restriction.invite.action.text',
		defaultMessage: 'Invite them',
		description: 'Action for user to change the restrictions on this content being shared',
	},
	inviteToConfluenceCta: {
		id: 'share-and-restrict-dialog.button.restriction.inviteToConfluence.action.text',
		defaultMessage: 'Invite to Confluence',
		description: 'Action for user to change the restrictions on this content being shared',
	},
	notifyUser: {
		id: 'share-and-restrict-dialog.button.restriction.notify.action.text',
		defaultMessage: 'Notify only',
		description: 'Action for the user to leave the restrictions of this content as is.',
	},
	inviteAndNotifyMessage: {
		id: 'share-and-restrict-dialog.button.restriction.message.inviteOrNotify.text',
		defaultMessage:
			'To view the shared page, <b>{userEmail}</b> <b> {userCount, plural, =0 {} one {and # other} other {and # others}}</b> might need Confluence access. Invite them to get started. If sharing with a group or list, you can choose to notify only.',
		description: 'Informs the user there might be restrictions on this content before sharing',
	},
	inviteUsersRegionLabel: {
		id: 'share-and-restrict-dialog.button.restriction.invite.region.label',
		defaultMessage: 'Invite users to Confluence',
		description: 'Accessibility label for the invite users section',
	},
});

type Principalable = {
	id: string;
	email: string;
};

type ResolvedPrincipals = {
	invited: Principalable[];
	skipped: Principalable[];
	all: Principalable[];
};

type InviteMessageProps = {
	userEmails: string[] | undefined | null;
	resolvedPrincipals: ResolvedPrincipals;
	setResolvedPrincipals: React.Dispatch<React.SetStateAction<ResolvedPrincipals>>;
	showEmailWarningMessage: boolean;
	markPrincipalsAsInvited: PrincipalsStagerProps['markPrincipalsAsInvited'];
	markPrincipalsAsSkipped: PrincipalsStagerProps['markPrincipalsAsSkipped'];
};

const inviteMessageContainer = xcss({
	padding: 'space.200',
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
});

const warningInviteMessageContainer = xcss({
	backgroundColor: 'color.background.warning',
	marginTop: 'space.200',
	marginLeft: 'space.200',
	marginRight: 'space.200',
});

const iconContainer = xcss({
	marginTop: 'space.050',
});

const Bold = (chunks: any) => <Text weight="bold">{chunks}</Text>;

export const InviteMessageComponent = ({
	userEmails,
	resolvedPrincipals,
	setResolvedPrincipals,
	showEmailWarningMessage,
	markPrincipalsAsInvited,
	markPrincipalsAsSkipped,
}: InviteMessageProps) => {
	const { openCoreInvites } = useContext(CoreInvitesContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId, userId } = useSessionData();
	const intl = useIntl();

	useEffect(() => {
		if (!openCoreInvites || !userEmails || userEmails?.length === 0) return;

		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'informInviteOnShare',
				attributes: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					inviteImprovementsM2Gate: fg('twcg-444-invite-usd-improvements-m2-gate'),
					// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
					inviteImprovementsM2Cohort: UNSAFE_expValNoExposure(
						'twcg-444-invite-usd-improvements-m2',
						'cohort',
						'not-enrolled',
					),
				},
			},
		}).fire();
	}, [createAnalyticsEvent, openCoreInvites, userEmails]);

	if (!openCoreInvites || !userEmails || userEmails?.length === 0) return null;

	const allResolvedEmails: Set<string> = new Set(
		resolvedPrincipals.all.map((email) => email.email),
	);
	const emailsToInvite = userEmails.filter((email) => !allResolvedEmails.has(email));

	const fireButtonClickedAnalytics = () =>
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'invitePeople',
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				attributes: {
					// eslint-disable-next-line confluence-feature-gating/no-exposure-recommendation
					inviteImprovementsM2Cohort: UNSAFE_expValNoExposure(
						'twcg-444-invite-usd-improvements-m2',
						'cohort',
						'not-enrolled',
					),
					emailCount: emailsToInvite.length,
				},
			},
		}).fire();

	const handleCtaClick = () => {
		openCoreInvites(
			CoreInvitesSource.SHARE_AND_RESTRICT_DIALOG,
			undefined,
			emailsToInvite.map((email) => ({
				id: email,
				email,
				name: email,
				type: 'email',
			})),
		);

		fireButtonClickedAnalytics();
	};

	const onInviteComplete: React.ComponentProps<typeof InlineInvite>['onInviteComplete'] = ({
		invited,
		requested,
		error,
	}) => {
		setResolvedPrincipals((prev: ResolvedPrincipals) => ({
			...prev,
			invited: [...prev.invited, ...invited, ...requested],
			all: [...prev.all, ...invited, ...requested],
		}));
		markPrincipalsAsInvited([...invited, ...requested], error);
		updateCachedInvites(invited.map(({ id }) => id));
		fireButtonClickedAnalytics();
	};

	const onDismiss = () => {
		const skippedPrincipals = emailsToInvite.map((email) => ({
			id: email,
			email,
		}));

		setResolvedPrincipals({
			...resolvedPrincipals,
			skipped: [...resolvedPrincipals.skipped, ...skippedPrincipals],
			all: [...resolvedPrincipals.all, ...skippedPrincipals],
		});
		markPrincipalsAsSkipped(emailsToInvite);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'notifyOnly',
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				attributes: {
					emailCount: emailsToInvite?.length,
				},
			},
		}).fire();
	};

	// When a11y_whiteboards_changes is enabled, wrap InlineInvite in a focusable container
	// to ensure keyboard accessibility
	const InlineInviteWithA11y = fg('a11y_whiteboards_changes') ? (
		<Box
			// Make the container keyboard focusable
			// This ensures the Invite button inside can receive focus via Tab key
			role="region"
			aria-label={intl.formatMessage(i18n.inviteUsersRegionLabel)}
		>
			<InlineInvite
				key={userEmails.join(',')}
				continueUrl={getCurrentUrl()}
				integration={SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE}
				touchpoint="Share Dialog"
				emails={emailsToInvite}
				product="confluence"
				onInviteComplete={onInviteComplete}
				onDismiss={onDismiss}
				atlassianAccountId={userId || ''}
				cloudId={cloudId}
			/>
		</Box>
	) : (
		<InlineInvite
			key={userEmails.join(',')}
			continueUrl={getCurrentUrl()}
			integration={SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE}
			touchpoint="Share Dialog"
			emails={emailsToInvite}
			product="confluence"
			onInviteComplete={onInviteComplete}
			onDismiss={onDismiss}
			atlassianAccountId={userId || ''}
			cloudId={cloudId}
		/>
	);

	return (
		// eslint-disable-next-line confluence-feature-gating/no-preconditioning
		fg('twcg-444-invite-usd-improvements-m2-gate') &&
			expValEquals('twcg-444-invite-usd-improvements-m2', 'cohort', 'variation') ? (
			InlineInviteWithA11y
		) : (
			<Inline
				alignBlock="start"
				space="space.200"
				xcss={[inviteMessageContainer, showEmailWarningMessage && warningInviteMessageContainer]}
			>
				<Box xcss={iconContainer}>
					{showEmailWarningMessage ? (
						<StatusWarningIcon label="" color={token('color.icon.warning')} />
					) : (
						<StatusInformationIcon label="" color={token('color.icon.subtle')} />
					)}
				</Box>
				<Stack alignInline="start" space="space.100">
					<Text align="start" color="color.text.subtle" size="medium">
						{showEmailWarningMessage ? (
							<FormattedMessage {...i18n.warningInviteMessage} />
						) : (
							<FormattedMessage
								{...i18n.inviteMessage}
								values={{
									userEmail: userEmails[0],
									userCount: userEmails?.length - 1,
									b: Bold,
								}}
							/>
						)}
					</Text>
					<Text color="color.link" weight="semibold" size="medium">
						<PressableLink isBlue onClick={handleCtaClick}>
							<FormattedMessage {...i18n.invitePeopleCta} />
						</PressableLink>
					</Text>
				</Stack>
			</Inline>
		)
	);
};

export const InviteMessage = (props: InviteMessageProps) => (
	<TransparentErrorBoundary attribution={Attribution.FLYWHEEL_ACTIVATION}>
		<InviteMessageComponent {...props} />
	</TransparentErrorBoundary>
);
